import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Badge,
} from '@mui/material';
import {
  Add,
  Refresh,
  FilterList,
  Sort,
  Download,
  Print,
  Share,
  MoreVert,
  KeyboardArrowDown,
  Bolt,
} from '@mui/icons-material';
import { useShortcut } from '../contexts/KeyboardShortcutsContext';

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  shortcut?: string;
  onClick: () => void;
  disabled?: boolean;
  badge?: number;
  divider?: boolean;
}

interface QuickActionsToolbarProps {
  onNewTicket?: () => void;
  onRefresh?: () => void;
  onFilter?: () => void;
  onSort?: () => void;
  onExport?: () => void;
  onPrint?: () => void;
  onShare?: () => void;
  customActions?: QuickAction[];
  loading?: boolean;
}

const QuickActionsToolbar: React.FC<QuickActionsToolbarProps> = ({
  onNewTicket,
  onRefresh,
  onFilter,
  onSort,
  onExport,
  onPrint,
  onShare,
  customActions = [],
  loading = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Register keyboard shortcuts
  useShortcut({
    key: 'n',
    description: 'Create new ticket',
    action: () => onNewTicket?.(),
  });

  useShortcut({
    key: 'r',
    description: 'Refresh data',
    action: () => onRefresh?.(),
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const defaultActions: QuickAction[] = [
    {
      id: 'new',
      label: 'New Ticket',
      icon: <Add fontSize="small" />,
      shortcut: 'N',
      onClick: () => {
        onNewTicket?.();
        handleClose();
      },
    },
    {
      id: 'refresh',
      label: 'Refresh',
      icon: <Refresh fontSize="small" />,
      shortcut: 'R',
      onClick: () => {
        onRefresh?.();
        handleClose();
      },
    },
    {
      id: 'filter',
      label: 'Filter',
      icon: <FilterList fontSize="small" />,
      onClick: () => {
        onFilter?.();
        handleClose();
      },
      divider: true,
    },
    {
      id: 'sort',
      label: 'Sort',
      icon: <Sort fontSize="small" />,
      onClick: () => {
        onSort?.();
        handleClose();
      },
    },
    {
      id: 'export',
      label: 'Export',
      icon: <Download fontSize="small" />,
      onClick: () => {
        onExport?.();
        handleClose();
      },
    },
    {
      id: 'print',
      label: 'Print',
      icon: <Print fontSize="small" />,
      onClick: () => {
        onPrint?.();
        handleClose();
      },
    },
    {
      id: 'share',
      label: 'Share',
      icon: <Share fontSize="small" />,
      onClick: () => {
        onShare?.();
        handleClose();
      },
    },
  ];

  const allActions = [...defaultActions, ...customActions];

  const primaryActions = allActions.slice(0, 3);
  const menuActions = allActions.slice(3);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      {/* Primary Actions - Always Visible */}
      {primaryActions.map((action) => (
        <Tooltip
          key={action.id}
          title={action.shortcut ? `${action.label} (${action.shortcut})` : action.label}
        >
          <Button
            variant={action.id === 'new' ? 'contained' : 'outlined'}
            size="small"
            startIcon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled || loading}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              ...(action.id === 'new' && {
                backgroundColor: 'var(--primary-600)',
                '&:hover': {
                  backgroundColor: 'var(--primary-700)',
                },
              }),
            }}
          >
            {action.label}
          </Button>
        </Tooltip>
      ))}

      {/* More Actions Menu */}
      {menuActions.length > 0 && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClick}
            endIcon={<KeyboardArrowDown />}
            sx={{ textTransform: 'none' }}
          >
            More
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: {
                backgroundColor: 'var(--bg-elevated)',
                border: '1px solid var(--border-light)',
                boxShadow: 'var(--shadow-lg)',
                minWidth: 180,
              },
            }}
          >
            {menuActions.map((action, index) => (
              <React.Fragment key={action.id}>
                {action.divider && index > 0 && (
                  <Divider sx={{ borderColor: 'var(--border-light)', my: 0.5 }} />
                )}
                <MenuItem
                  onClick={action.onClick}
                  disabled={action.disabled}
                  sx={{
                    color: 'var(--text-primary)',
                    '&:hover': {
                      backgroundColor: 'var(--bg-hover)',
                    },
                    '&.Mui-disabled': {
                      color: 'var(--text-muted)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'var(--text-secondary)', minWidth: 32 }}>
                    {action.badge ? (
                      <Badge badgeContent={action.badge} color="error">
                        {action.icon}
                      </Badge>
                    ) : (
                      action.icon
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={action.label}
                    secondary={action.shortcut}
                    slotProps={{
                      primary: {
                        sx: { fontSize: '14px' }
                      },
                      secondary: {
                        sx: {
                          fontSize: '11px',
                          color: 'var(--text-tertiary)',
                          fontFamily: 'var(--font-mono)',
                        }
                      }
                    }}
                  />
                </MenuItem>
              </React.Fragment>
            ))}
          </Menu>
        </>
      )}

      {/* Quick Actions Badge */}
      <Tooltip title="Quick Actions Available">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            ml: 1,
            px: 1,
            py: 0.5,
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-secondary)',
            fontSize: '11px',
          }}
        >
          <Bolt sx={{ fontSize: 14, color: 'var(--warning-500)' }} />
          <span>Press ? for shortcuts</span>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default QuickActionsToolbar;
